import { useState, useEffect } from 'react'
import { onSnapshot, collection, query, where } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { useAuth } from '../../contexts/AuthContext'
import {
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  Trash2,
  Database,
  Mail,
  Bomb,
  FileX,
  RefreshCw
} from 'lucide-react'

interface NukeStep {
  id: string
  label: string
  icon: React.ReactNode
  status: 'pending' | 'active' | 'complete' | 'error'
  detail?: string
  count?: number
}

interface NukeResult {
  deleted: number
  operationId?: string
  details?: {
    vendorName: string
    vendorDomain: string
    totalScanned: number
    matchedByVendorId: number
    matchedByDomain: number
    matchedBySender: number
    deletedFromImap: number
    deletedFromFirestore: number
    promotionsDeleted: number
    updatesDeleted: number
    deepSearchFound?: number
    deepSearchDeleted?: number
    deepSearchCycles?: number
    deepSearchSamples?: string[]
    totalImapDeleted?: number
  }
}

interface NukeProgressModalProps {
  isOpen: boolean
  vendorId: string
  vendorName: string
  onClose: () => void
  onConfirm: () => Promise<NukeResult>
  onComplete: () => void
}

export default function NukeProgressModal({
  isOpen,
  vendorId,
  vendorName,
  onClose,
  onConfirm,
  onComplete
}: NukeProgressModalProps) {
  const { user } = useAuth()
  const [stage, setStage] = useState<'confirm' | 'running' | 'complete' | 'error'>('confirm')
  const [steps, setSteps] = useState<NukeStep[]>([])
  const [result, setResult] = useState<NukeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStage('confirm')
      setSteps([])
      setResult(null)
      setError(null)
    }
  }, [isOpen])

  // Listen to Firestore for progress updates
  useEffect(() => {
    if (!user || stage !== 'running') return

    // Listen to all nuke progress for this vendor (simpler query, no index needed)
    // Filter client-side for the most recent
    const progressQuery = query(
      collection(db, 'users', user.uid, 'nukeProgress'),
      where('vendorId', '==', vendorId)
    )

    const unsubscribe = onSnapshot(progressQuery, (snapshot) => {
      if (snapshot.empty) {
        console.log('NUKE Progress: No documents found yet')
        return
      }

      // Find the most recent document (sort client-side)
      let mostRecent = snapshot.docs[0]
      let mostRecentTime = 0

      for (const doc of snapshot.docs) {
        const data = doc.data()
        const updatedAt = data.updatedAt?.toMillis?.() || 0
        if (updatedAt > mostRecentTime) {
          mostRecentTime = updatedAt
          mostRecent = doc
        }
      }

      const progress = mostRecent.data()

      console.log('NUKE Progress update:', progress.step, progress.detail)

      // Update steps based on progress
      const counts = progress.counts || {}

      setSteps(prev => {
        const newSteps = [...prev]

        // Update based on step
        switch (progress.step) {
          case 'initializing':
          case 'scanning':
            updateStepInArray(newSteps, 'scan-cache', { status: 'active', detail: progress.detail })
            break

          case 'found-cached':
            updateStepInArray(newSteps, 'scan-cache', {
              status: 'complete',
              detail: `Found ${counts.cachedFound || 0} cached emails`,
              count: counts.cachedFound
            })
            updateStepInArray(newSteps, 'delete-cache-firestore', { status: 'active' })
            break

          case 'deleting-cached':
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', {
              status: 'complete',
              detail: `Found ${counts.cachedFound || 0} to delete`,
              count: counts.cachedFound
            })
            updateStepInArray(newSteps, 'delete-cache-gmail', {
              status: 'active',
              detail: progress.detail
            })
            break

          case 'deleted-cached':
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-gmail', {
              status: 'complete',
              detail: `Removed ${counts.cachedDeleted || 0} from Gmail`,
              count: counts.cachedDeleted
            })
            break

          case 'deep-searching':
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-gmail', { status: 'complete' })
            updateStepInArray(newSteps, 'deep-search', {
              status: 'active',
              detail: progress.detail || 'Searching all Gmail...'
            })
            break

          case 'deep-deleted':
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-gmail', { status: 'complete' })
            updateStepInArray(newSteps, 'deep-search', {
              status: 'complete',
              detail: `Found ${counts.deepSearchFound || 0} historical emails`,
              count: counts.deepSearchFound
            })
            updateStepInArray(newSteps, 'deep-delete', {
              status: 'complete',
              detail: `Deleted ${counts.deepSearchDeleted || 0} emails in ${counts.deepSearchCycles || 1} cycle${(counts.deepSearchCycles || 1) > 1 ? 's' : ''}`,
              count: counts.deepSearchDeleted
            })
            break

          case 'cleanup':
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-gmail', { status: 'complete' })
            updateStepInArray(newSteps, 'deep-search', { status: 'complete' })
            updateStepInArray(newSteps, 'deep-delete', { status: 'complete' })
            updateStepInArray(newSteps, 'cleanup', {
              status: 'active',
              detail: progress.detail
            })
            break

          case 'complete':
            // Mark all steps complete and update with final counts
            updateStepInArray(newSteps, 'scan-cache', { status: 'complete' })
            updateStepInArray(newSteps, 'delete-cache-firestore', {
              status: 'complete',
              detail: `Found ${counts.cachedFound || 0} from ${vendorName}`,
              count: counts.cachedFound
            })
            updateStepInArray(newSteps, 'delete-cache-gmail', {
              status: 'complete',
              detail: `Removed ${counts.cachedDeleted || 0} from Gmail`,
              count: counts.cachedDeleted
            })
            updateStepInArray(newSteps, 'deep-search', {
              status: 'complete',
              detail: counts.deepSearchFound
                ? `Found ${counts.deepSearchFound} historical emails`
                : 'No additional historical emails found',
              count: counts.deepSearchFound
            })
            updateStepInArray(newSteps, 'deep-delete', {
              status: 'complete',
              detail: counts.deepSearchDeleted
                ? `Deleted ${counts.deepSearchDeleted} emails in ${counts.deepSearchCycles || 1} cycle${(counts.deepSearchCycles || 1) > 1 ? 's' : ''}`
                : 'Nothing to delete',
              count: counts.deepSearchDeleted
            })
            updateStepInArray(newSteps, 'cleanup', {
              status: 'complete',
              detail: `Removed ${counts.promotionsDeleted || 0} promos, ${counts.updatesDeleted || 0} updates`,
              count: (counts.promotionsDeleted || 0) + (counts.updatesDeleted || 0)
            })

            // Set complete result from Firestore
            if (progress.result) {
              setResult(progress.result)
              setStage('complete')
            }
            break
        }

        return newSteps
      })

      // Handle error status
      if (progress.status === 'error') {
        setError(progress.detail || 'An error occurred')
        setStage('error')
      }
    }, (err) => {
      console.error('Error listening to nuke progress:', err)
    })

    return () => unsubscribe()
  }, [user, stage, vendorId, vendorName])

  const initializeSteps = (): NukeStep[] => [
    {
      id: 'scan-cache',
      label: 'Scanning cached emails (last 30 days)',
      icon: <Database size={18} />,
      status: 'pending'
    },
    {
      id: 'delete-cache-firestore',
      label: 'Removing from local cache',
      icon: <FileX size={18} />,
      status: 'pending'
    },
    {
      id: 'delete-cache-gmail',
      label: 'Removing cached emails from Gmail',
      icon: <Mail size={18} />,
      status: 'pending'
    },
    {
      id: 'deep-search',
      label: 'Deep searching Gmail for ALL historical emails',
      icon: <Search size={18} />,
      status: 'pending'
    },
    {
      id: 'deep-delete',
      label: 'Deleting historical emails (500 at a time)',
      icon: <Trash2 size={18} />,
      status: 'pending'
    },
    {
      id: 'cleanup',
      label: 'Cleaning up promotions & updates',
      icon: <FileX size={18} />,
      status: 'pending'
    }
  ]

  const updateStepInArray = (stepsArray: NukeStep[], stepId: string, updates: Partial<NukeStep>) => {
    const idx = stepsArray.findIndex(s => s.id === stepId)
    if (idx >= 0) {
      stepsArray[idx] = { ...stepsArray[idx], ...updates }
    }
  }

  const handleConfirm = async () => {
    setStage('running')
    const initialSteps = initializeSteps()
    setSteps(initialSteps)

    // Mark first step as active
    initialSteps[0].status = 'active'
    setSteps([...initialSteps])

    try {
      // Call the nuke function - it will update Firestore with progress
      // We listen to Firestore for real-time updates
      const nukeResult = await onConfirm()

      // If we got a result directly (fast operation or timeout handled gracefully)
      if (nukeResult && nukeResult.details) {
        setResult(nukeResult)
        setStage('complete')

        // Update all steps to complete
        setSteps(prev => prev.map(step => ({
          ...step,
          status: 'complete' as const
        })))
      }
      // If result came through Firestore listener, it will set stage='complete'
    } catch (err) {
      console.error('NUKE failed:', err)

      // Check if it's a timeout error - the operation might still be running
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete emails'

      if (errorMessage.includes('deadline') || errorMessage.includes('timeout') || errorMessage.includes('DEADLINE')) {
        // Don't show error - keep listening to Firestore for progress
        console.log('Request timed out but operation may still be running...')
        // Keep stage as 'running' and continue listening to Firestore
        return
      }

      setError(errorMessage)
      setStage('error')

      // Mark remaining active/pending steps as error
      setSteps(prev => prev.map(step =>
        step.status === 'active' || step.status === 'pending'
          ? { ...step, status: 'error' as const }
          : step
      ))
    }
  }

  const handleClose = () => {
    if (stage === 'complete') {
      onComplete()
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${
          stage === 'error' ? 'bg-red-600' :
          stage === 'complete' ? 'bg-green-600' :
          'bg-red-600'
        }`}>
          <div className="flex items-center gap-3 text-white">
            <Bomb size={24} />
            <h2 className="text-lg font-semibold">
              {stage === 'confirm' && 'Delete All Emails'}
              {stage === 'running' && 'Deleting Emails...'}
              {stage === 'complete' && 'NUKE Complete'}
              {stage === 'error' && 'Error'}
            </h2>
          </div>
          {stage !== 'running' && (
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Confirmation Stage */}
          {stage === 'confirm' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-red-800">
                  <p className="font-semibold mb-2">
                    This will permanently delete ALL emails from "{vendorName}"
                  </p>
                  <ul className="space-y-1 text-red-700">
                    <li>- Delete all cached emails (last 30 days)</li>
                    <li>- Deep search Gmail for ALL historical emails</li>
                    <li>- Delete in batches of 500 until complete</li>
                    <li>- This action cannot be undone</li>
                  </ul>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800 flex items-center gap-2">
                  <RefreshCw size={14} />
                  <span>
                    <strong>How it works:</strong> Search All Mail &rarr; Delete up to 500 &rarr; Repeat until all gone
                  </span>
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Bomb size={16} />
                  Delete All Emails
                </button>
              </div>
            </div>
          )}

          {/* Running/Complete/Error Stage */}
          {(stage === 'running' || stage === 'complete' || stage === 'error') && (
            <div className="space-y-4">
              {/* Progress Steps */}
              <div className="space-y-2">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      step.status === 'active' ? 'bg-blue-50 border border-blue-200' :
                      step.status === 'complete' ? 'bg-green-50 border border-green-200' :
                      step.status === 'error' ? 'bg-red-50 border border-red-200' :
                      'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    {/* Status Icon */}
                    <div className={`flex-shrink-0 ${
                      step.status === 'active' ? 'text-blue-600' :
                      step.status === 'complete' ? 'text-green-600' :
                      step.status === 'error' ? 'text-red-600' :
                      'text-gray-400'
                    }`}>
                      {step.status === 'active' ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : step.status === 'complete' ? (
                        <CheckCircle2 size={18} />
                      ) : step.status === 'error' ? (
                        <AlertCircle size={18} />
                      ) : (
                        step.icon
                      )}
                    </div>

                    {/* Label & Detail */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        step.status === 'active' ? 'text-blue-900' :
                        step.status === 'complete' ? 'text-green-900' :
                        step.status === 'error' ? 'text-red-900' :
                        'text-gray-500'
                      }`}>
                        {step.label}
                      </p>
                      {step.detail && (
                        <p className={`text-xs ${
                          step.status === 'complete' ? 'text-green-700' :
                          step.status === 'active' ? 'text-blue-600' :
                          step.status === 'error' ? 'text-red-700' :
                          'text-gray-500'
                        }`}>
                          {step.detail}
                        </p>
                      )}
                    </div>

                    {/* Count Badge */}
                    {step.count !== undefined && step.count > 0 && (
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                        step.status === 'complete' ? 'bg-green-200 text-green-800' :
                        step.status === 'active' ? 'bg-blue-200 text-blue-800' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {step.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Summary */}
              {stage === 'complete' && result?.details && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h3 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-2 border border-green-100">
                      <p className="text-xs text-green-600">Total from Gmail</p>
                      <p className="text-lg font-bold text-green-800">
                        {(result.details.totalImapDeleted || result.details.deletedFromImap).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-green-100">
                      <p className="text-xs text-green-600">From cache</p>
                      <p className="text-lg font-bold text-green-800">
                        {result.details.deletedFromFirestore.toLocaleString()}
                      </p>
                    </div>
                    {result.details.deepSearchFound !== undefined && result.details.deepSearchFound > 0 && (
                      <>
                        <div className="bg-white rounded-lg p-2 border border-green-100">
                          <p className="text-xs text-green-600">Historical found</p>
                          <p className="text-lg font-bold text-green-800">
                            {result.details.deepSearchFound.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-2 border border-green-100">
                          <p className="text-xs text-green-600">Historical deleted</p>
                          <p className="text-lg font-bold text-green-800">
                            {(result.details.deepSearchDeleted || 0).toLocaleString()}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {result.details.deepSearchCycles && result.details.deepSearchCycles > 1 && (
                    <p className="mt-2 text-xs text-green-600">
                      Completed in {result.details.deepSearchCycles} search cycles
                    </p>
                  )}
                  {result.details.deepSearchSamples && result.details.deepSearchSamples.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-green-600 mb-1">Sample deleted:</p>
                      <p className="text-xs text-green-800 truncate">
                        {result.details.deepSearchSamples[0]}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {stage === 'error' && error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-800 font-medium mb-1">Operation failed</p>
                  <p className="text-sm text-red-700">{error}</p>
                  <p className="text-xs text-red-600 mt-2">
                    Some emails may have been deleted before the error occurred.
                  </p>
                </div>
              )}

              {/* Close Button */}
              {(stage === 'complete' || stage === 'error') && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleClose}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      stage === 'complete' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {stage === 'complete' ? 'Done' : 'Close'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
